import { useEffect, useState } from "react";
import { Loader2, Sparkles, GitCommit, ShieldCheck, Network, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/ui";
import { cn } from "@/lib/utils";
import InsightCard from "@/components/ai/InsightCard";

const sevColor = (s) => ({ Critical: "#F04438", High: "#F04438", Medium: "#F79009", Low: "#12B76A" }[s] || "#155EEF");

export default function AIInsights() {
  const [insights, setInsights] = useState(null);
  const [events, setEvents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [corr, setCorr] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recs, setRecs] = useState("");
  const [recLoading, setRecLoading] = useState(false);

  useEffect(() => {
    api.get("/insights").then((r) => setInsights(r.data)).catch(() => setInsights([]));
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/intel/correlations").then((r) => setCorr(r.data)).catch(() => {});
    api.get("/intel/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const genRecs = async () => {
    setRecLoading(true);
    try { const { data } = await api.post("/intel/recommendations"); setRecs(data.recommendations); }
    catch (e) { toast.error("AI recommendation unavailable right now"); }
    finally { setRecLoading(false); }
  };

  if (!insights) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  return (
    <div>
      <PageHeader overline="AI Governance · Human-in-the-Loop" title="AI Insights"
        description="Every recommendation follows WHAT · WHY · EVIDENCE · CONFIDENCE · ACTION with a required human reviewer." />

      {/* Phase 9 — Cross-Module Intelligence */}
      <div className="mb-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Network size={18} className="text-[#7C3AED]" /><h2 className="text-lg font-bold tracking-tight">Cross-Module Intelligence</h2>
            <span className="rounded-full bg-[#7C3AED]/10 px-2 py-0.5 text-[11px] font-medium text-[#7C3AED]">Phase 9</span></div>
          <button data-testid="generate-recs-btn" onClick={genRecs} disabled={recLoading}
            className="flex items-center gap-2 rounded-md bg-[#7C3AED] px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#6D28D9] disabled:opacity-60">
            {recLoading ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} Generate AI Recommendations
          </button>
        </div>

        {recs && (
          <div data-testid="ai-recommendations" className="mb-4 rounded-xl border bg-card p-5 shadow-sm" style={{ borderLeft: "3px solid #7C3AED" }}>
            <div className="mb-2 flex items-center gap-2 text-sm font-bold"><Sparkles size={15} className="text-[#7C3AED]" /> AI Cross-Module Recommendations</div>
            <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-foreground/90">{recs}</p>
          </div>
        )}

        {!corr && <div className="flex h-24 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {corr?.correlations?.map((c) => (
            <div key={c.id} data-testid={`correlation-${c.id}`} className="rounded-xl border bg-card p-4 shadow-sm">
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className="text-sm font-bold">{c.title}</span>
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: sevColor(c.severity) }}>{c.severity}</span>
              </div>
              <div className="mb-2 flex flex-wrap gap-1.5">{c.modules.map((mm) => <span key={mm} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{mm}</span>)}</div>
              <p className="text-[13px] leading-snug"><span className="font-semibold">What: </span>{c.what}</p>
              <p className="mt-1 text-xs text-muted-foreground"><span className="font-semibold text-foreground/80">Why: </span>{c.why}</p>
              <p className="mt-1 text-xs text-muted-foreground"><span className="font-semibold text-foreground/80">Evidence: </span>{c.evidence}</p>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-[#7C3AED]" style={{ width: `${c.confidence}%` }} /></div>
                <span className="text-[11px] font-semibold text-[#7C3AED]">{c.confidence}%</span>
              </div>
              <p className="mt-2 rounded-md bg-[#155EEF]/5 px-2.5 py-1.5 text-xs"><span className="font-semibold text-[#155EEF]">Action: </span>{c.action}</p>
            </div>
          ))}
          {corr && corr.correlations.length === 0 && <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">No cross-module correlations detected right now — signals are within normal range.</div>}
        </div>

        {analytics && (
          <div className="mt-4 rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3 text-sm font-bold">Module Activity ({analytics.total_events} events on the bus)</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {analytics.module_load.map((ml) => (
                <div key={ml.module} className="rounded-lg border bg-background p-3"><div className="text-xl font-bold text-[#155EEF]">{ml.count}</div><div className="mt-0.5 overline">{ml.module}</div></div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mb-3 flex items-center gap-2"><Sparkles size={17} className="text-[#7C3AED]" /><h2 className="text-lg font-bold tracking-tight">Recommendations</h2></div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {insights.map((ins, i) => <InsightCard key={ins.id} insight={ins} index={i} />)}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-bold"><GitCommit size={15} className="text-[#155EEF]" /> Event Bus <span className="ml-1 font-normal text-muted-foreground">(cross-module)</span></div>
          <div className="careiq-scroll max-h-96 divide-y overflow-y-auto">
            {events.map((e) => (
              <div key={e.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-[#155EEF]">{e.event_type}</span>
                  <span className="text-[11px] text-muted-foreground">{String(e.timestamp).slice(11, 19)}</span>
                </div>
                <div className="text-xs text-muted-foreground">by {e.actor}</div>
              </div>
            ))}
            {events.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No events yet — take an action (administer a med, assign a shift) to see the bus in motion.</div>}
          </div>
        </div>

        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-bold"><ShieldCheck size={15} className="text-[#12B76A]" /> Audit Log <span className="ml-1 font-normal text-muted-foreground">(tamper-resistant)</span></div>
          <div className="careiq-scroll max-h-96 divide-y overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{a.action}</span>
                  <span className="text-[11px] text-muted-foreground">{String(a.timestamp).slice(11, 19)}</span>
                </div>
                <div className="text-xs text-muted-foreground">{a.actor} · {a.entity_type}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
